import { useEffect, useRef } from 'react';

/**
 * Fires onTimeout after `timeoutMs` of no user activity (mouse move/click,
 * keypress, touch, scroll) - same activity set SessionTimeoutProvider
 * already uses for the main app's 30-minute timeout, for consistency.
 * Any activity resets the clock. Pass `enabled: false` to disable entirely
 * (e.g. only apply this on screens where nothing is in progress yet, never
 * on the active exam screen itself - that's governed by its own duration).
 *
 * `deadlineStorageKey`, when given, also persists "now + timeoutMs" to that
 * localStorage key on every reset. This is what makes the timeout survive
 * closing the tab: the in-memory setTimeout above is destroyed the instant
 * the tab closes and can't tell how long it was closed for, but a stored
 * wall-clock deadline can. The corresponding session-token state (in
 * AuthContext) checks this same key against Date.now() at load time, before
 * ever treating the stored token as valid - see entranceToken/
 * examCenterToken's useState initializers.
 */
export function useIdleTimeout(
  timeoutMs: number,
  onTimeout: () => void,
  enabled: boolean = true,
  deadlineStorageKey?: string,
  storage: Storage = localStorage
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Kept in a ref so the effect's event listeners don't need to be torn
  // down/re-attached every time the caller passes a new onTimeout closure.
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    if (!enabled) return;

    const reset = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => onTimeoutRef.current(), timeoutMs);
      if (deadlineStorageKey) {
        storage.setItem(deadlineStorageKey, String(Date.now() + timeoutMs));
      }
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((event) => window.addEventListener(event, reset));
    reset();

    return () => {
      events.forEach((event) => window.removeEventListener(event, reset));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [timeoutMs, enabled, deadlineStorageKey, storage]);
}

/** Checks a deadline written by useIdleTimeout above against the current
 * clock - true if it's missing (never started) or still in the future,
 * false only if a real deadline has actually passed. Used at token-load
 * time so a session that expired while the tab was closed doesn't get
 * treated as valid just because it's still sitting in storage. */
export function isDeadlineValid(deadlineStorageKey: string, storage: Storage = localStorage): boolean {
  const raw = storage.getItem(deadlineStorageKey);
  if (!raw) return true;
  const deadline = Number(raw);
  return Number.isNaN(deadline) || Date.now() <= deadline;
}
